import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ChevronDown, FileText, Mic, MicOff } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { fetchUserMediaThunk, updateMicrophoneSourceThunk } from '../../thunk';
import { updateSelectedDevices, setMicrophoneAudio } from '../../recorderSlice';
import { RootState } from '../../store';
import MicIconWithAudioVisualizer from '../deviceSelection/MicIconWithAudioVisualizer';

interface ControlButtonsProps {
  onToggleNotes: () => void;
  showNotes: boolean;
  isPopupMode?: boolean;
}

const ControlButtons: React.FC<ControlButtonsProps> = ({
  onToggleNotes,
  showNotes,
  isPopupMode = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { status, selectedDevices, availableDevices } = useSelector(
    (state: RootState) => state.recorder,
  );

  const isRecording = status === 'recording';
  const isPaused = status === 'paused';
  const isControlDisabled = !['recording', 'paused'].includes(status);

  const handleDeviceChange = async (deviceId: string) => {
    try {
      // Update the selected device in the store
      dispatch(updateSelectedDevices({ audioinput: deviceId }));

      const micEnabled = deviceId !== 'none';

      // Fetch new media stream with the selected device BEFORE enabling microphoneAudio
      // so the audio detection useEffect finds the stream ready
      await dispatch(
        fetchUserMediaThunk({
          audio: deviceId === 'none' ? false : { deviceId },
        }),
      );

      // If recording is active, update the recorder's microphone source
      if (isRecording || isPaused) {
        await dispatch(updateMicrophoneSourceThunk());
      }

      // Enable/disable microphoneAudio AFTER stream is ready
      // This triggers audio detection which needs the stream to be available
      dispatch(setMicrophoneAudio(micEnabled));

      // Update localStorage
      const currentSettings = JSON.parse(
        localStorage.getItem('recorderSettings') || '{}',
      );
      const updatedSettings = {
        ...currentSettings,
        selectedDevices: {
          ...currentSettings.selectedDevices,
          audioinput: deviceId,
        },
      };
      localStorage.setItem('recorderSettings', JSON.stringify(updatedSettings));
    } catch (error) {
      console.error('Error changing audio device:', error);
    }
  };

  // Note: buttonSize, iconSize, stopIconSize removed as we now use fixed sizes matching live recorder

  return (
    <TooltipProvider>
      {/* Button Group - Pill Container */}
      <div className="flex items-center px-1.5 gap-0.5">
        {/* Notes Button - Only in fullscreen mode */}
        {!isPopupMode && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onToggleNotes}
                  className={cn(
                    'flex items-center justify-center w-10 h-10 transition-opacity duration-200',
                    'cursor-pointer hover:[&>svg]:scale-110',
                  )}
                >
                  <FileText
                    className={cn(
                      'h-5 w-5 transition-transform duration-200',
                      showNotes ? 'text-primary' : 'text-muted-foreground',
                    )}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {showNotes
                  ? t('recorderControls.tooltips.hideNotes', 'Hide Notes')
                  : t('recorderControls.tooltips.showNotes', 'Show Notes')}
              </TooltipContent>
            </Tooltip>

            <div className="w-px h-5 bg-border opacity-80 mx-0.5" />
          </>
        )}

        {/* Microphone Selector */}
        <Select
          value={selectedDevices.audioinput.deviceId || 'none'}
          onValueChange={handleDeviceChange}
          disabled={isControlDisabled}
        >
          <SelectTrigger
            className="w-auto h-10 border-0 bg-transparent shadow-none px-1.5"
            icon={<ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />}
          >
            <div className="flex items-center gap-2">
              {selectedDevices.audioinput.deviceId === 'none' ||
              !selectedDevices.audioinput.deviceId ? (
                <MicOff className="h-5 w-5 text-muted-foreground" />
              ) : (
                <MicIconWithAudioVisualizer
                  deviceId={selectedDevices.audioinput.deviceId}
                  className="h-5 w-5"
                />
              )}
            </div>
          </SelectTrigger>
          <SelectContent className="z-[9999]">
            <SelectItem value="none">
              <div className="flex items-center gap-2">
                <MicOff className="h-5 w-5 text-muted-foreground" />
                <span>
                  {t(
                    'deviceSelection.microphone.dontUse',
                    "Don't use microphone",
                  )}
                </span>
              </div>
            </SelectItem>
            {availableDevices.audioinput.map((device) => (
              <SelectItem key={device.deviceId} value={device.deviceId}>
                <div className="flex items-center gap-2">
                  <Mic className="h-5 w-5 text-primary" />
                  <span>{device.label || `Microphone ${device.deviceId}`}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </TooltipProvider>
  );
};

export default ControlButtons;
